import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Select } from 'components/ui/Select'
import { emailConfigApi } from 'lib/api'
import { useTranslation } from 'react-i18next'
import {
  filterEmailTemplatesForDisplay,
  getEmailTemplateDisplayLanguage,
  getEmailTemplateDisplayName,
} from 'lib/emailTemplates'
import { Mail, X } from 'lucide-react'

interface SendEmailModalProps {
  isOpen: boolean
  onClose: () => void
  candidateEmail: string
  candidateName: string
  candidateId: number
  onEmailSent?: () => void
}

export function SendEmailModal({
  isOpen,
  onClose,
  candidateEmail,
  candidateName,
  candidateId,
  onEmailSent
}: SendEmailModalProps) {
  const { t, i18n } = useTranslation()

  const displayLanguage = useMemo(
    () => getEmailTemplateDisplayLanguage(i18n.language),
    [i18n.language]
  )
  const [emailType, setEmailType] = useState<'template' | 'custom'>('template')
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null)
  const [templates, setTemplates] = useState<Array<{ id: number, name: string, subject: string }>>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true)
    try {
      const templatesData = await emailConfigApi.getAllTemplates({ language: displayLanguage })
      setTemplates(
        filterEmailTemplatesForDisplay(
          templatesData.filter((template) => template.is_active),
          displayLanguage
        ).map((template) => ({
          id: template.id,
          name: getEmailTemplateDisplayName(template, displayLanguage, t),
          subject: template.subject,
        }))
      )
    } catch (error) {
      console.error('Error loading templates:', error)
      setTemplates([])
    } finally {
      setLoadingTemplates(false)
    }
  }, [displayLanguage, t])

  useEffect(() => {
    if (isOpen) {
      loadTemplates()
    }
  }, [isOpen, loadTemplates])

  const handleTemplateChange = async (templateId: number) => {
    setSelectedTemplate(templateId)
    try {
      const template = await emailConfigApi.getTemplate(templateId)
      setSubject(template.data.subject)
      setBody(template.data.body)
    } catch (error) {
      console.error('Error loading template:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (emailType === 'template') {
        if (!selectedTemplate) {
          setError(t('candidateDetail.sendEmailModal.selectTemplate'))
          setLoading(false)
          return
        }
        
        await emailConfigApi.sendCandidateEmail({
          candidate_id: candidateId,
          template_id: selectedTemplate
        })
      } else {
        if (!subject.trim() || !body.trim()) {
          setError(t('candidateDetail.sendEmailModal.fillRequired'))
          setLoading(false)
          return
        }
        
        await emailConfigApi.sendCandidateEmail({
          candidate_id: candidateId,
          subject: subject.trim(),
          body: body.trim()
        })
      }

      // Reset form
      setSelectedTemplate(null)
      setSubject('')
      setBody('')
      setEmailType('template')
      
      if (onEmailSent) {
        onEmailSent()
      }
      
      onClose()
    } catch (error: any) {
      setError(error.response?.data?.error || t('candidateDetail.sendEmailModal.sendFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('candidateDetail.sendEmailModal.title')}
      className="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Type Selection */}
        <div>
          <label className="block text-sm font-medium mb-3 text-foreground">
            {t('candidateDetail.sendEmailModal.emailType')}
          </label>
          <div className="flex space-x-3">
            <Button
              type="button"
              variant={emailType === 'template' ? 'default' : 'outline'}
              onClick={() => setEmailType('template')}
              className={`flex-1 transition-all duration-200 ${
                emailType === 'template'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md'
                  : 'hover:bg-accent'
              }`}
            >
              <Mail className="h-4 w-4 mr-2" />
              {t('candidateDetail.sendEmailModal.useTemplate')}
            </Button>
            <Button
              type="button"
              variant={emailType === 'custom' ? 'default' : 'outline'}
              onClick={() => setEmailType('custom')}
              className={`flex-1 transition-all duration-200 ${
                emailType === 'custom'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md'
                  : 'hover:bg-accent'
              }`}
            >
              <Mail className="h-4 w-4 mr-2" />
              {t('candidateDetail.sendEmailModal.customEmail')}
            </Button>
          </div>
        </div>

        {/* Template Selection */}
        {emailType === 'template' && (
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('candidateDetail.sendEmailModal.selectTemplate')} *
            </label>
            {loadingTemplates ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : (
              <Select
                value={selectedTemplate?.toString() || ''}
                onChange={(e) => handleTemplateChange(parseInt(e.target.value))}
                options={templates.map(t => ({ value: t.id.toString(), label: t.name }))}
                placeholder={t('candidateDetail.sendEmailModal.chooseTemplate')}
              />
            )}
            {selectedTemplate && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium mb-2">{t('candidateDetail.sendEmailModal.preview')}:</div>
                  <div className="mb-2">
                    <span className="font-medium">{t('candidateDetail.sendEmailModal.subject')}:</span>
                    <div className="mt-1 text-gray-700 dark:text-gray-300">{subject}</div>
                  </div>
                  <div>
                    <span className="font-medium">{t('candidateDetail.sendEmailModal.body')}:</span>
                    <div className="mt-1 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{body}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Custom Email Fields */}
        {emailType === 'custom' && (
          <>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('candidateDetail.sendEmailModal.subject')} *
              </label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('candidateDetail.sendEmailModal.subjectPlaceholder')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('candidateDetail.sendEmailModal.body')} *
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t('candidateDetail.sendEmailModal.bodyPlaceholder')}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                rows={10}
                required
              />
              <div className="mt-2 text-xs text-muted-foreground">
                {t('candidateDetail.sendEmailModal.variablesHint')}: {'{candidate_name}'}, {'{candidate_email}'}, {'{company_name}'}
              </div>
            </div>
          </>
        )}

        {/* Recipient Info */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="text-sm">
            <div className="font-medium mb-1">{t('candidateDetail.sendEmailModal.to')}:</div>
            <div className="text-muted-foreground">
              {candidateName} &lt;{candidateEmail}&gt;
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="px-6"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={loading || (emailType === 'template' && !selectedTemplate)}
            className="px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 dark:from-blue-500 dark:to-blue-600 dark:hover:from-blue-600 dark:hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                {t('candidateDetail.sendEmailModal.sending')}
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                {t('candidateDetail.sendEmailModal.send')}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

